import { useState, useRef, useEffect } from "react";
import { Button } from "../ui/button";
import {
  Settings,
  User,
  LogOut,
  ChevronDown,
  ToggleLeft,
  ToggleRight,
  Type,
  Palette,
} from "lucide-react";
import { useAuth } from "@workos-inc/authkit-react";
import { useNavigate } from "react-router";
import { useAdminMode } from "../../contexts/AdminModeContext";
import { useColor } from "../../contexts/ColorContext";
import { appConfig } from "@/appConfig";

interface SettingsDropdownProps {
  onOpenProfileModal: () => void;
  currentUser: {
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    email: string;
    profile_image_url?: string | null;
  } | null;
}

const SettingsDropdown = ({
  onOpenProfileModal,
  currentUser,
}: SettingsDropdownProps) => {
  const { role, signOut } = useAuth();
  const { adminMode, toggleAdminMode } = useAdminMode();
  const { themeColor, setThemeColor, logoName, setLogoName } = useColor();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showLogoChanger, setShowLogoChanger] = useState(false);
  const [newLogoName, setNewLogoName] = useState(logoName);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleLogout = () => {
    signOut();
    setIsOpen(false);
  };

  const handleProfileImage = () => {
    onOpenProfileModal();
    setIsOpen(false);
  };

  const handleModeSwitch = () => {
    const newMode = adminMode === "admin" ? "member" : "admin";
    toggleAdminMode();

    // Navigate to appropriate page based on new mode
    if (newMode === "member") {
      navigate("/assistant");
    } else {
      navigate("/documents");
    }
    setIsOpen(false);
  };

  const handleColorChange = (color: string) => {
    setThemeColor(color);
  };

  const toggleColorPicker = () => {
    setShowColorPicker(!showColorPicker);
    setShowLogoChanger(false);
  };

  const toggleLogoChanger = () => {
    setShowLogoChanger(!showLogoChanger);
    setShowColorPicker(false);
    setNewLogoName(logoName);
  };

  const handleLogoNameChange = () => {
    if (newLogoName.trim()) {
      setLogoName(newLogoName.trim());
      setShowLogoChanger(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setShowColorPicker(false);
        setShowLogoChanger(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Settings Button - icon only on mobile */}
      <Button
        variant="outline"
        size="sm"
        className="text-sm px-3 py-1 rounded-full flex items-center gap-2 border-gray-300 bg-transparent hover:bg-slate-100 text-gray-700 hover:text-gray-700 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <Settings className="w-4 h-4" />
        <span className="hidden lg:inline">Einstellungen</span>
        <ChevronDown
          className={`w-4 h-4 transition-transform hidden lg:block ${isOpen ? "rotate-180" : ""}`}
        />
      </Button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-50">
          {/* User Info */}
          {currentUser && (
            <>
              <div className="px-4 py-2 text-sm text-gray-900 border-b border-gray-100">
                <div className="font-medium">
                  {currentUser.first_name && currentUser.last_name
                    ? `${currentUser.first_name} ${currentUser.last_name}`
                    : currentUser.email}
                </div>
                <div className="text-gray-500 text-xs">{currentUser.email}</div>
              </div>
            </>
          )}

          {/* Profile Option */}
          <button
            onClick={handleProfileImage}
            className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-slate-100 transition-colors text-left bg-transparent border-none focus:outline-none active:outline-none"
          >
            <User className="w-4 h-4 text-gray-500" />
            Profil bearbeiten
          </button>

          {/* Logo Name Changer */}
          {appConfig.features.isDemoMode ? (
            <div className="relative">
              <button
                onClick={toggleLogoChanger}
                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-slate-100 transition-colors text-left bg-transparent border-none focus:outline-none active:outline-none"
              >
                <Type className="w-4 h-4 text-gray-500" />
                Logo Name ändern
                <span
                  className="ml-auto text-xs text-gray-400 truncate max-w-[80px]"
                  title={logoName}
                >
                  {logoName}
                </span>
              </button>

              {/* Logo Name Changer Dropdown */}
              {showLogoChanger && (
                <div className="absolute right-full top-0 mr-1 bg-white rounded-md shadow-lg border border-gray-200 p-3 z-50 min-w-[200px]">
                  <div className="text-sm font-medium text-gray-700 mb-2">
                    Logo Name ändern
                  </div>
                  <input
                    type="text"
                    value={newLogoName}
                    onChange={(e) => setNewLogoName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-3"
                    placeholder="Neuer Logo Name"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleLogoNameChange();
                      }
                    }}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={handleLogoNameChange}
                      className="flex-1 px-3 py-1 text-sm text-white rounded-md transition-colors"
                      style={{ backgroundColor: themeColor }}
                    >
                      Speichern
                    </button>
                    <button
                      onClick={() => setShowLogoChanger(false)}
                      className="flex-1 px-3 py-1 text-sm text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                    >
                      Abbrechen
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : null}

          {/* Color Theme Option */}
          {appConfig.features.isDemoMode ? (
            <div className="relative">
              <button
                onClick={toggleColorPicker}
                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-slate-100 transition-colors text-left bg-transparent border-none focus:outline-none active:outline-none"
              >
                <Palette className="w-4 h-4 text-gray-500" />
                Farbe ändern
                <div
                  className="w-4 h-4 rounded-full border border-gray-300 ml-auto"
                  style={{ backgroundColor: themeColor }}
                />
              </button>

              {/* Color Picker Dropdown */}
              {showColorPicker && (
                <div className="absolute right-full top-0 mr-1 bg-white rounded-md shadow-lg border border-gray-200 p-3 z-50">
                  <div className="text-sm font-medium text-gray-700 mb-2">
                    Farbe wählen
                  </div>
                  <input
                    type="color"
                    value={themeColor}
                    onChange={(e) => handleColorChange(e.target.value)}
                    className="w-full h-10 rounded border border-gray-300 cursor-pointer"
                  />
                </div>
              )}
            </div>
          ) : null}

          {/* Mode Switch Option - Only for Admins */}
          {role === "admin" && (
            <button
              onClick={handleModeSwitch}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-slate-100 transition-colors text-left bg-transparent border-none focus:outline-none active:outline-none"
            >
              {adminMode === "admin" ? (
                <ToggleRight className="w-4 h-4 text-gray-500" />
              ) : (
                <ToggleLeft className="w-4 h-4 text-gray-500" />
              )}
              Zu {adminMode === "admin" ? "Assistent" : "Admin"} wechseln
            </button>
          )}

          {/* Divider */}
          <div className="border-t border-gray-100 my-1" />

          {/* Logout Option */}
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-slate-100 transition-colors text-left bg-transparent border-none focus:outline-none active:outline-none"
          >
            <LogOut className="w-4 h-4 text-gray-500" />
            Abmelden
          </button>
        </div>
      )}
    </div>
  );
};

export default SettingsDropdown;
